
import { useEffect } from 'react';

interface MetadataProps {
  title: string;
  description: string;
  image: string;
  url?: string;
}

export const useMetadata = ({ title, description, image, url }: MetadataProps) => {
  useEffect(() => {
    // Create absolute URL for image if it's a relative path
    const getAbsoluteImageUrl = (imagePath: string) => {
      if (!imagePath) return '';
      
      // If already absolute URL, return as is
      if (imagePath.startsWith('http')) {
        return imagePath;
      }
      
      // Create absolute URL
      const origin = window.location.origin;
      const absoluteUrl = imagePath.startsWith('/') 
        ? `${origin}${imagePath}` 
        : `${origin}/${imagePath}`;
      
      console.log(`useMetadata: Converting ${imagePath} to ${absoluteUrl}`);
      return absoluteUrl;
    };
    
    // Get absolute image URL
    const absoluteImageUrl = getAbsoluteImageUrl(image);
    
    console.log(`Setting metadata for page: ${title}`, {
      title,
      description,
      image,
      absoluteImageUrl
    });
    
    // Immediately update the document title for quick feedback
    document.title = title;
    
    // Use requestAnimationFrame to ensure DOM updates happen on the next paint cycle
    requestAnimationFrame(() => {
      // Update basic metadata
      document.title = title; // Set title again to ensure it takes effect
      
      // Find or create meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', description);

      // Update Open Graph metadata
      const metaTags = {
        'og:title': title,
        'og:description': description,
        'og:image': absoluteImageUrl,
        'og:url': url || window.location.href,
        'og:type': 'website',
        'twitter:card': 'summary_large_image',
        'twitter:title': title,
        'twitter:description': description,
        'twitter:image': absoluteImageUrl
      };

      // Create or update meta tags
      Object.entries(metaTags).forEach(([property, content]) => {
        // For og:image, ensure it's always an absolute URL
        if (property === 'og:image' || property === 'twitter:image') {
          content = absoluteImageUrl;
        }
        
        // Try to find existing tag by property attribute (normal for OG tags)
        let metaTag = document.querySelector(`meta[property="${property}"]`);
        
        if (!metaTag) {
          // If not found by property, try name (for twitter cards)
          metaTag = document.querySelector(`meta[name="${property}"]`);
          
          if (!metaTag) {
            // If still not found, create new tag
            metaTag = document.createElement('meta');
            
            // Set appropriate attribute based on tag type
            if (property.startsWith('og:')) {
              metaTag.setAttribute('property', property);
            } else {
              metaTag.setAttribute('name', property);
            }
            
            document.head.appendChild(metaTag);
          }
        }
        
        // Set content
        metaTag.setAttribute('content', content);
      });
      
      // Add canonical URL
      let canonicalTag = document.querySelector('link[rel="canonical"]');
      if (!canonicalTag) {
        canonicalTag = document.createElement('link');
        canonicalTag.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalTag);
      }
      canonicalTag.setAttribute('href', url || window.location.href);
      
      // Log the metadata for debugging
      console.log('Metadata updated:', { 
        title, 
        description, 
        image: absoluteImageUrl,
        url: url || window.location.href
      });
    });

    // Cleanup function
    return () => {
      // We don't need to clean up meta tags as they should persist and be updated by the next page
    };
  }, [title, description, image, url]); // Re-run effect when these values change
};
